/**
 * Specialist Detail Page
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Edit as EditSlotIcon,
  Delete as DeleteSlotIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Star as StarIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { PageHeader, ConfirmDialog } from '../../components/common';
import {
  useGetSpecialistQuery,
  useDeleteSpecialistMutation,
  useActivateSpecialistMutation,
  useDeactivateSpecialistMutation,
} from '../../store/api/specialistsApi';
import {
  useGetAvailabilitySlotsQuery,
  useCreateAvailabilitySlotMutation,
  useUpdateAvailabilitySlotMutation,
  useDeleteAvailabilitySlotMutation,
  AvailabilitySlot,
} from '../../store/api/availabilitySlotsApi';
import moment from 'moment';
import toast from 'react-hot-toast';
import { usePermissions } from '../../hooks/usePermissions';
import ProviderProfileDetails from '../../components/providers/ProviderProfileDetails';
import ProviderDocumentsSection from '../../components/providers/ProviderDocumentsSection';

const SpecialistDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canManageSpecialists = hasPermission('manage_specialists');

  const { data: specialistData, isLoading, error } = useGetSpecialistQuery(Number(id!), {
    skip: !id,
  });

  const { data: slotsData, isLoading: slotsLoading } = useGetAvailabilitySlotsQuery(
    {
      specialist_id: Number(id!),
      page: 1,
      page_size: 100,
    },
    { skip: !id }
  );

  const [deleteSpecialist, { isLoading: isDeleting }] = useDeleteSpecialistMutation();
  const [activateSpecialist] = useActivateSpecialistMutation();
  const [deactivateSpecialist] = useDeactivateSpecialistMutation();
  const [createSlot, { isLoading: isCreatingSlot }] = useCreateAvailabilitySlotMutation();
  const [updateSlot, { isLoading: isUpdatingSlot }] = useUpdateAvailabilitySlotMutation();
  const [deleteSlot] = useDeleteAvailabilitySlotMutation();

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [slotDialog, setSlotDialog] = useState(false);
  const [editingSlot, setEditingSlot] = useState<AvailabilitySlot | null>(null);
  const [slotFormData, setSlotFormData] = useState({
    days: [] as string[],
    start_time: '09:00',
    end_time: '17:00',
  });

  const specialist = specialistData?.data;

  const daysOfWeek = [
    { value: 'monday', label: t('specialists.days.monday') },
    { value: 'tuesday', label: t('specialists.days.tuesday') },
    { value: 'wednesday', label: t('specialists.days.wednesday') },
    { value: 'thursday', label: t('specialists.days.thursday') },
    { value: 'friday', label: t('specialists.days.friday') },
    { value: 'saturday', label: t('specialists.days.saturday') },
    { value: 'sunday', label: t('specialists.days.sunday') },
  ];

  const handleDelete = async () => {
    if (!specialist) return;

    try {
      await deleteSpecialist(specialist.id).unwrap();
      toast.success(t('specialists.toast.deleted'));
      navigate('/specialists');
    } catch (err: any) {
      toast.error(err?.data?.message || t('specialists.toast.deleteFailed'));
    }
  };

  const handleActivate = async () => {
    if (!specialist) return;

    try {
      await activateSpecialist(specialist.id).unwrap();
      toast.success(t('specialists.toast.activated'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('specialists.toast.activateFailed'));
    }
  };

  const handleDeactivate = async () => {
    if (!specialist) return;

    try {
      await deactivateSpecialist(specialist.id).unwrap();
      toast.success(t('specialists.toast.deactivated'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('specialists.toast.deactivateFailed'));
    }
  };

  const handleOpenSlotDialog = (slot?: AvailabilitySlot) => {
    if (slot) {
      setEditingSlot(slot);
      // Extract days from existing slots for this specialist
      const existingDays = slotsData?.results
        ?.filter((s) => s.specialist === specialist?.id && s.start_time === slot.start_time && s.end_time === slot.end_time)
        .map((s) => s.day_name.toLowerCase()) || [];
      setSlotFormData({
        days: existingDays,
        start_time: slot.start_time.substring(0, 5),
        end_time: slot.end_time.substring(0, 5),
      });
    } else {
      setEditingSlot(null);
      setSlotFormData({
        days: [],
        start_time: '09:00',
        end_time: '17:00',
      });
    }
    setSlotDialog(true);
  };

  const handleCloseSlotDialog = () => {
    setSlotDialog(false);
    setEditingSlot(null);
    setSlotFormData({
      days: [],
      start_time: '09:00',
      end_time: '17:00',
    });
  };

  const handleSlotSubmit = async () => {
    if (!specialist || slotFormData.days.length === 0) {
      toast.error(t('specialists.toast.selectDay'));
      return;
    }

    try {
      if (editingSlot) {
        // For editing, we need to update the slot
        await updateSlot({
          id: editingSlot.id,
          data: {
            specialist: specialist.id,
            days: slotFormData.days,
            start_time: slotFormData.start_time,
            end_time: slotFormData.end_time,
          },
        }).unwrap();
        toast.success(t('specialists.toast.slotUpdated'));
      } else {
        await createSlot({
          specialist: specialist.id,
          days: slotFormData.days,
          start_time: slotFormData.start_time,
          end_time: slotFormData.end_time,
        }).unwrap();
        toast.success(t('specialists.toast.slotsCreated'));
      }
      handleCloseSlotDialog();
    } catch (err: any) {
      toast.error(err?.data?.message || t('specialists.toast.slotSaveFailed'));
    }
  };

  const handleDeleteSlot = async (slotId: number) => {
    try {
      await deleteSlot(slotId).unwrap();
      toast.success(t('specialists.toast.slotDeleted'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('specialists.toast.slotDeleteFailed'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !specialist) {
    const err = error as { status?: number; data?: { message?: string } } | undefined;
    const isForbidden = err?.status === 403;
    const message = isForbidden
      ? err?.data?.message || t('specialists.errors.forbidden')
      : t('specialists.errors.notFound');

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          {message}
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/specialists')}
          sx={{ mt: 2 }}
        >
          {t('specialists.buttons.backToList')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('specialists.detail.title')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.specialists'), path: '/specialists' },
          { label: specialist.full_name },
        ]}
        action={
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {canManageSpecialists && (
              <>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/specialists/${specialist.id}/edit`)}
                >
                  {t('common.edit')}
                </Button>
                {specialist.is_active ? (
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleDeactivate}
                  >
                    {t('specialists.buttons.deactivate')}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleActivate}
                  >
                    {t('specialists.buttons.activate')}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialog(true)}
                >
                  {t('common.delete')}
                </Button>
              </>
            )}
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/specialists')}
            >
              {t('specialists.buttons.back')}
            </Button>
          </Box>
        }
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
        {/* Specialist Overview */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                src={specialist.profile_picture || undefined}
                alt={specialist.full_name}
                sx={{ width: 80, height: 80, mr: 3 }}
              >
                {specialist.full_name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {specialist.full_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                  <Chip
                    label={t(`specialists.status.${specialist.status}`)}
                    size="small"
                    color={getStatusColor(specialist.status) as any}
                  />
                  <Chip
                    icon={specialist.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                    label={specialist.is_active ? t('specialists.status.ACTIVE') : t('specialists.status.INACTIVE')}
                    size="small"
                    color={specialist.is_active ? 'success' : 'default'}
                  />
                  {specialist.is_verified && (
                    <Chip label={t('specialists.badge.verified')} size="small" color="success" />
                  )}
                </Box>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* Personal Information Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('specialists.detail.personalInfo')}
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('specialists.detail.emailAddress')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {specialist.email || '—'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('specialists.detail.phoneNumber')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {specialist.phone_number}
                    </Typography>
                  </Box>
                </Grid>
                {specialist.age && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {t('specialists.detail.age')}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight={500}>
                        {t('specialists.detail.ageYears', { age: specialist.age })}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Professional Information Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <WorkIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('specialists.detail.professionalInfo')}
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WorkIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('specialists.detail.specialization')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {specialist.specialization || '—'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <StarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('specialists.detail.rating')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      ⭐ {parseFloat(specialist.rating).toFixed(1)} ({t('specialists.detail.reviewsCount', { count: specialist.total_reviews })})
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AssignmentIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('specialists.detail.totalOrders')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {specialist.total_orders}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('specialists.detail.completedOrders')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {specialist.completed_orders}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Metadata Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('specialists.detail.metadata')}
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CalendarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('specialists.detail.createdAt')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {moment(specialist.created_at).format('MMMM DD, YYYY HH:mm')}
                    </Typography>
                  </Box>
                </Grid>
                {specialist.approved_at && (
                  <>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper',
                          height: '100%',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CheckCircleIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            {t('specialists.detail.approvedAt')}
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={500}>
                          {moment(specialist.approved_at).format('MMMM DD, YYYY HH:mm')}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </CardContent>
        </Card>

        {/* Contact preferences, coverage, clinic, usual working hours */}
        <ProviderProfileDetails provider={specialist} />

        {/* Availability Slots */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('specialists.detail.availabilitySlots')}
                </Typography>
              </Box>
              {canManageSpecialists && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenSlotDialog()}
                >
                  {t('specialists.buttons.addSlots')}
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />
            {slotsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : slotsData?.results && slotsData.results.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('specialists.slots.date')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('specialists.slots.day')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('specialists.slots.time')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('specialists.slots.duration')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('specialists.slots.status')}</TableCell>
                      {canManageSpecialists && (
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          {t('common.actions')}
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slotsData.results.map((slot) => (
                      <TableRow key={slot.id}>
                        <TableCell>{moment(slot.date).format('MMM DD, YYYY')}</TableCell>
                        <TableCell>{slot.day_name}</TableCell>
                        <TableCell>
                          {moment(slot.start_time, 'HH:mm:ss').format('h:mm A')} - {moment(slot.end_time, 'HH:mm:ss').format('h:mm A')}
                        </TableCell>
                        <TableCell>{t('specialists.slots.durationMinutes', { count: slot.slot_duration })}</TableCell>
                        <TableCell>
                          <Chip
                            label={slot.is_active ? t('specialists.status.ACTIVE') : t('specialists.status.INACTIVE')}
                            size="small"
                            color={slot.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        {canManageSpecialists && (
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenSlotDialog(slot)}
                              color="primary"
                            >
                              <EditSlotIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteSlot(slot.id)}
                              color="error"
                            >
                              <DeleteSlotIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('specialists.detail.noSlots')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <ProviderDocumentsSection
        kind="specialists"
        providerId={specialist.id}
        canManage={canManageSpecialists}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('specialists.dialog.deleteTitle')}
        message={t('specialists.dialog.deleteMessage', { name: specialist.full_name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        loading={isDeleting}
      />

      {/* Availability Slot Dialog */}
      <Dialog open={slotDialog} onClose={handleCloseSlotDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSlot ? t('specialists.dialog.editSlot') : t('specialists.dialog.addSlots')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{t('specialists.dialog.days')}</InputLabel>
            <Select
              multiple
              value={slotFormData.days}
              onChange={(e) => setSlotFormData({ ...slotFormData, days: e.target.value as string[] })}
              label={t('specialists.dialog.days')}
              renderValue={(selected) => (selected as string[]).map((day) => daysOfWeek.find((d) => d.value === day)?.label).join(', ')}
            >
              {daysOfWeek.map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  <Checkbox checked={slotFormData.days.indexOf(day.value) > -1} />
                  {day.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="time"
            label={t('specialists.dialog.startTime')}
            value={slotFormData.start_time}
            onChange={(e) => setSlotFormData({ ...slotFormData, start_time: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="time"
            label={t('specialists.dialog.endTime')}
            value={slotFormData.end_time}
            onChange={(e) => setSlotFormData({ ...slotFormData, end_time: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSlotDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSlotSubmit}
            disabled={slotFormData.days.length === 0 || isCreatingSlot || isUpdatingSlot}
          >
            {isCreatingSlot || isUpdatingSlot ? t('specialists.buttons.saving') : editingSlot ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpecialistDetailPage;

